import { useState } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taOutreachApi';
import type { OutreachCandidate } from '../../types/taOutreach';

/** How many updates run at once: a redraft is two model calls (~25 s), one after the other was ~55 s for two. */
const UPDATE_PARALLEL = 3;

/**
 * Write & send › "Update all drafts (N)" (Sofia, Oct 27 2026): the card's Update for every draft
 * that needs one, like "Write missing drafts". Hidden when there is none. Since Oct 28 2026 also
 * the viewer's own drafts written for another sender or caller (`sentAsStale`), and a colleague's
 * drafts signed for them (`sentAsOther`, Sofia, Oct 28 2026: the button said "2 / 2" while every
 * card still said Luisa) — those are signed for the viewer, no model. Up to three at once.
 */
export function UpdateAllDrafts({
  rows,
  run,
  onUpdated,
  redraft,
}: {
  rows: OutreachCandidate[];
  run: (ids: string[], each: (id: string) => Promise<void>, opts?: { parallel?: number }) => Promise<unknown>;
  onUpdated: (item: OutreachCandidate) => void;
  redraft: (id: string) => Promise<OutreachCandidate | null>;
}) {
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // One row per draft: a draft can be both outdated and signed for someone else.
  const unique = [...new Map(rows.map((c) => [c.id, c])).values()];
  if (!unique.length && !progress) return null;

  /**
   * One update: a draft for another sender is put together again first (no model) and redrafted
   * only when that cannot be done (`needsRedraft`); a draft with an older version is redrafted.
   * Answers whether an approved draft lost its approval (a redraft is a new text, CodeRabbit, PR #758).
   */
  async function updateOne(c: OutreachCandidate): Promise<boolean> {
    if ((c.sentAsStale || c.sentAsOther) && !c.draftOutdated) {
      try {
        // Own drafts: today's choice (apply-template); a colleague's: signed for the viewer (`/sign`).
        const r = c.sentAsOther ? await api.signOutreachDraft(c.id) : await api.applyOutreachTemplate(c.id, c.inmailTemplate ?? null);
        if (r.item) {
          onUpdated(r.item);
          return c.status === 'ready' && r.item.status !== 'ready';
        }
        if (r.refusal) {
          showToast({ status: 'error', position: 'bottomCenter', description: `${c.name}: ${r.refusal}` });
          return false;
        }
      } catch (err) {
        showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not update.' });
        return false;
      }
    }
    const item = await redraft(c.id);
    return c.status === 'ready' && Boolean(item) && item?.status !== 'ready';
  }

  async function updateAll() {
    const todo = unique;
    let unapproved = 0;
    setProgress({ done: 0, total: todo.length });
    try {
      await run(
        todo.map((c) => c.id),
        async (id) => {
          const c = todo.find((x) => x.id === id);
          if (c && (await updateOne(c))) unapproved += 1;
          setProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
        },
        { parallel: UPDATE_PARALLEL }
      );
    } finally {
      setProgress(null);
    }
    if (unapproved) {
      showToast({
        status: 'info',
        position: 'bottomCenter',
        description: `${unapproved} approved draft${unapproved === 1 ? ' was' : 's were'} rewritten. Check and approve again.`,
      });
    }
  }
  return (
    <button
      type="button"
      className="h-8 rounded-md border border-solid border-[#1A1A1A] bg-white px-3.5 text-xs font-semibold hover:bg-[#1A1A1A] hover:text-white disabled:opacity-60"
      disabled={Boolean(progress)}
      onClick={() => void updateAll()}
    >
      {progress ? `Updating… ${Math.min(progress.done, progress.total)} of ${progress.total} done` : `Update all drafts (${unique.length})`}
    </button>
  );
}
